import React from 'react';
import { Link as MuiLink, LinkProps as MuiLinkProps } from '@mui/material';
import {
  BOX_BUTTON_SIZE_SPEC,
  BOX_BUTTON_VARIANT_TOKENS,
  type BoxButtonSize,
  type BoxButtonVariant,
} from '@/theme/buttonTokens';

/**
 * 박스 버튼처럼 보이는 링크(a 태그). 색상/사이즈는 BoxButton 과 같은 값(theme/buttonTokens.ts)을 사용.
 * 메뉴 드로어의 이동 버튼(급여명세서 등)에 쓰여서 기본으로 가로를 꽉 채운다.
 */

export type LinkProps = Omit<MuiLinkProps, 'variant' | 'size'> & {
  readonly variant: BoxButtonVariant;
  readonly size?: BoxButtonSize;
};

const Link: React.FC<LinkProps> = ({
  children,
  variant,
  size = 'large',
  sx,
  ...props
}) => {
  const tokens = BOX_BUTTON_VARIANT_TOKENS[variant];
  const spec = BOX_BUTTON_SIZE_SPEC[size];
  return (
    <MuiLink
      {...props}
      sx={{
        boxSizing: 'border-box',
        display: 'flex',
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        height: `${spec.height}px`,
        padding: `0 ${spec.paddingX}px`,
        borderRadius: '8px',
        border: `1px solid ${tokens.enabled.border}`,
        backgroundColor: tokens.enabled.bg,
        color: tokens.enabled.color,
        fontSize: `${spec.fontSize}px`,
        lineHeight: `${spec.lineHeight}px`,
        fontWeight: 400,
        textDecoration: 'none',
        textAlign: 'center',
        cursor: 'pointer',
        transition:
          'background-color 120ms ease, border-color 120ms ease, color 120ms ease',
        '&:hover, &:focus, &:focus-visible': {
          backgroundColor: tokens.hover.bg,
          borderColor: tokens.hover.border,
          color: tokens.hover.color,
          textDecoration: 'none',
          outline: 'none',
        },
        '&:active': {
          backgroundColor: tokens.pressed.bg,
          borderColor: tokens.pressed.border,
          color: tokens.pressed.color,
        },
        ...sx,
      }}
    >
      {children}
    </MuiLink>
  );
};

export default Link;
